"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  Headphones,
  Mail,
  MessageCircle,
  Search,
  FileText,
  UserCheck,
} from "lucide-react";
import {
  SUPPORT_FAQS,
  SUPPORT_HOURS_LABEL,
  SUPPORT_KAKAO_HANDLE,
  SUPPORT_KAKAO_URL,
  SUPPORT_NOTICES,
  SUPPORT_PLATFORM_NOTICE,
  SUPPORT_EMAIL,
} from "@/lib/config/customerSupport";

export default function HelpPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const filteredFaqs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return SUPPORT_FAQS;
    return SUPPORT_FAQS.filter(
      faq =>
        faq.question.toLowerCase().includes(q) ||
        faq.answer.toLowerCase().includes(q) ||
        faq.tags.some(tag => tag.toLowerCase().includes(q))
    );
  }, [searchQuery]);

  return (
    <div className="min-h-screen bg-[#f4f5f7] pb-28 md:pb-16">
      {/* Hero */}
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
          <p className="text-sm font-semibold text-violet-600">커넥톤 고객센터</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            안녕하세요, 커넥톤 고객센터입니다
            <br className="hidden sm:block" />
            <span className="text-gray-700"> 무엇을 도와드릴까요?</span>
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-gray-500 sm:text-base">
            결제·피드백·레슨 문의는 아래에서 검색하거나 1:1 상담으로 남겨 주세요.
          </p>

          <label className="relative mt-8 block max-w-2xl">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="궁금한 내용을 검색해 보세요 (결제, 피드백, 레슨…)"
              className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-4 pl-12 pr-4 text-sm text-gray-900 outline-none ring-violet-500 transition focus:bg-white focus:ring-2"
            />
          </label>

          <p className="mt-4 flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <Clock className="h-3.5 w-3.5" />
            상담 시간 {SUPPORT_HOURS_LABEL}
          </p>

          <p className="mt-3 text-xs leading-relaxed text-gray-400">
            {SUPPORT_PLATFORM_NOTICE}
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl space-y-6 px-4 py-8 sm:px-6">
        {/* Role split */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Link
            href="/contact?audience=customer"
            className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:border-violet-300 hover:shadow-md"
          >
            <div className="flex items-center gap-3">
              <span className="rounded-xl bg-violet-50 p-3 text-violet-600">
                <Headphones className="h-5 w-5" />
              </span>
              <div>
                <p className="font-bold text-gray-900">고객 1:1 문의</p>
                <p className="mt-0.5 text-xs text-gray-500">
                  결제 · 환불 · 피드백 · 레슨
                </p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </Link>
          <Link
            href="/contact?audience=pro"
            className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:border-teal-300 hover:shadow-md"
          >
            <div className="flex items-center gap-3">
              <span className="rounded-xl bg-teal-50 p-3 text-teal-700">
                <UserCheck className="h-5 w-5" />
              </span>
              <div>
                <p className="font-bold text-gray-900">프로 지원 센터</p>
                <p className="mt-0.5 text-xs text-gray-500">
                  정산 · 출금 · 배정 · 프로필
                </p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </Link>
        </div>

        {/* Consultation channels */}
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-lg font-bold text-gray-900">상담 채널</h2>
          <p className="mt-1 text-sm text-gray-500">
            급한 확인은 카카오톡, 기록·환불·증빙이 필요하면 1:1 문의를 이용해 주세요.
          </p>

          <div className="mt-5 space-y-3">
            <Link
              href="/contact"
              className="flex items-start justify-between gap-4 rounded-xl border border-violet-100 bg-violet-50/60 p-4 transition hover:bg-violet-50"
            >
              <div className="flex gap-3">
                <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-violet-600" />
                <div>
                  <p className="font-semibold text-gray-900">1:1 문의 접수</p>
                  <p className="mt-1 text-xs text-gray-500">
                    {SUPPORT_HOURS_LABEL}
                  </p>
                  <p className="mt-1 text-xs text-violet-700">
                    접수 후 &apos;내 문의함&apos;에서 처리 상태를 확인할 수 있습니다.
                  </p>
                </div>
              </div>
              <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-violet-400" />
            </Link>

            <a
              href={SUPPORT_KAKAO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-4 transition hover:bg-gray-50"
            >
              <div className="flex gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#FEE500] text-[10px] font-bold text-[#191919]">
                  K
                </span>
                <div>
                  <p className="font-semibold text-gray-900">
                    카카오톡 빠른 상담 {SUPPORT_KAKAO_HANDLE}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    실시간 응대가 필요할 때 · {SUPPORT_HOURS_LABEL}
                  </p>
                </div>
              </div>
              <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-gray-400" />
            </a>

            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-4 transition hover:bg-gray-50"
            >
              <div className="flex gap-3">
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-gray-600" />
                <div>
                  <p className="font-semibold text-gray-900">이메일 상담</p>
                  <p className="mt-1 text-xs text-gray-500">{SUPPORT_EMAIL}</p>
                </div>
              </div>
              <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-gray-400" />
            </a>

            <Link
              href="/profile/inquiries"
              className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-4 transition hover:bg-gray-50"
            >
              <div className="flex gap-3">
                <FileText className="mt-0.5 h-5 w-5 shrink-0 text-gray-600" />
                <div>
                  <p className="font-semibold text-gray-900">내 문의함</p>
                  <p className="mt-1 text-xs text-gray-500">
                    접수·처리 중·완료 상태를 확인합니다
                  </p>
                </div>
              </div>
              <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-gray-400" />
            </Link>
          </div>
        </section>

        {/* Notices */}
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">공지사항</h2>
            <Link href="/terms" className="text-xs font-medium text-gray-400 hover:text-gray-600">
              이용약관
            </Link>
          </div>
          <ul className="mt-4 divide-y divide-gray-100">
            {SUPPORT_NOTICES.map(notice => (
              <li key={notice.id}>
                <Link
                  href={notice.href || "/help"}
                  className="flex items-center justify-between gap-3 py-3 text-sm hover:text-violet-700"
                >
                  <span className="min-w-0 truncate font-medium text-gray-800">
                    {notice.title}
                  </span>
                  <span className="shrink-0 text-xs text-gray-400">{notice.date}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-lg font-bold text-gray-900">자주 묻는 질문</h2>
          <div className="mt-4 space-y-2">
            {filteredFaqs.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">
                검색 결과가 없습니다. 1:1 문의로 남겨 주세요.
              </p>
            ) : (
              filteredFaqs.map(faq => (
                <div
                  key={faq.id}
                  className="overflow-hidden rounded-xl border border-gray-200"
                >
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-4 py-3.5 text-left hover:bg-gray-50"
                    onClick={() =>
                      setExpandedFaq(expandedFaq === faq.id ? null : faq.id)
                    }
                  >
                    <span className="pr-3 text-sm font-medium text-gray-900">
                      {faq.question}
                    </span>
                    {expandedFaq === faq.id ? (
                      <ChevronUp className="h-4 w-4 shrink-0 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                    )}
                  </button>
                  {expandedFaq === faq.id && (
                    <div className="border-t border-gray-100 px-4 py-3 text-sm leading-relaxed text-gray-600">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </section>

        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-gray-400">
          <Link href="/terms" className="hover:text-gray-600">
            이용약관
          </Link>
          <Link href="/privacy" className="hover:text-gray-600">
            개인정보취급방침
          </Link>
          <Link href="/about" className="hover:text-gray-600">
            서비스 소개
          </Link>
        </div>
      </div>
    </div>
  );
}
